import {
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DefaultColumnResponse } from '../services/responses/default-columns.response';
import { DefaultColumnService } from '../services/default-columns.service';
import { DefaultColumnRow } from './default-columns.row';
import { useDefaultColumnsReorder } from '../hooks/use-default-columns-reorder.hook';
import { setLoading, useMaterialUIController } from 'context';
import { ITableProps } from '@types_main/itable.props';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';

export function DefaultColumnTable(props: ITableProps<DefaultColumnResponse>) {
    const { t } = useTranslation();
    const { data, editAction } = props;
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    const { rows, reorder } = useDefaultColumnsReorder(data?.data?.records, props.refresh);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    function doDelete(id: string) {
        const service = new DefaultColumnService();
        setLoading(dispatch, true);
        service
            .deleteById(id)
            .then(() => {
                showAlert(t('defaultColumns.excluirSucesso'), 'success', 2500);
                props.setRefresh(!props.refresh);
            })
            .catch((err) => {
                showAlert(err.response?.data?.message ?? t('defaultColumns.excluirErro'), 'error', 3000);
            })
            .finally(() => {
                setLoading(dispatch, false);
            });
    }

    async function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        try {
            await reorder(String(active.id), String(over.id));
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
            props.setRefresh(!props.refresh);
        }
    }

    return (
        <Grid>
            <TableContainer
                sx={{
                    marginTop: '15px',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                }}
            >
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="15%">
                                {t('defaultColumns.ordem')}
                            </TableCell>
                            <TableCell width="65%">
                                {t('general.nome')}
                            </TableCell>
                            <TableCell width="20%" align="center">
                                {t('general.acoes')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            rows.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                    // Avisos de leitor de tela são <div>: dentro de <tbody> o HTML fica inválido.
                                    accessibility={{ container: document.body }}
                                >
                                    <SortableContext
                                        items={rows.map((item) => item.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {rows.map((item) => (
                                            <DefaultColumnRow
                                                key={item.id}
                                                item={item}
                                                onEdit={() => editAction(item.id)}
                                                onDelete={() =>
                                                    alertDelete(
                                                        item.id,
                                                        doDelete,
                                                        item.name
                                                    )
                                                }
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={3} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, index) => index).map((item, index) => (
                                <TableRow hover key={index}>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
